import {
  type FC,
  type KeyboardEvent,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { Badge } from "./Badge";
import { Text } from "./Text";

export interface SearchableOptionItem {
  value: string;
  label: string;
  description?: string | null;
  meta?: string | null;
  keywords?: string[];
  badge?: string | null;
  disabled?: boolean;
}

interface SearchableOptionListProps {
  items: SearchableOptionItem[];
  selectedValue?: string | null;
  onSelect: (value: string) => void;
  label?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  maxHeightClassName?: string;
  /**
   * Tightens row padding, description size and the search bar for dialogs that
   * stack several of these lists. Opt-in rather than a new default: three other
   * screens use this component at the roomier scale and should not shift.
   */
  dense?: boolean;
}

function resolveOptionStateClass(options: {
  isActive: boolean;
  isSelected: boolean;
}) {
  if (options.isSelected) {
    return "border-emerald-400 bg-emerald-50/70 dark:border-emerald-500/40 dark:bg-emerald-500/10";
  }
  if (options.isActive) {
    return "border-slate-400 bg-slate-50 dark:border-slate-600 dark:bg-slate-900";
  }
  return "border-transparent bg-transparent hover:border-slate-300 hover:bg-slate-50 dark:hover:border-slate-700 dark:hover:bg-slate-900/70";
}

const SearchableOptionRow: FC<{
  activeIndex: number;
  dense?: boolean;
  disabled: boolean;
  index: number;
  item: SearchableOptionItem;
  onHover: (index: number) => void;
  onSelect: (value: string) => void;
  selectedValue?: string | null;
}> = ({
  activeIndex,
  disabled,
  dense = false,
  index,
  item,
  onHover,
  onSelect,
  selectedValue,
}) => {
  const isSelected = item.value === selectedValue;
  const isActive = index === activeIndex;
  const isDisabled = disabled || item.disabled === true;
  const optionStateClass = resolveOptionStateClass({ isActive, isSelected });

  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      data-option-index={index}
      disabled={isDisabled}
      onClick={() => onSelect(item.value)}
      onMouseEnter={() => onHover(index)}
      className={`flex w-full items-start rounded-none border text-left transition-colors ${
        dense ? "gap-2 px-2 py-1" : "gap-3 px-3 py-2"
      } ${optionStateClass} ${
        isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      }`.trim()}
    >
      <span
        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-none border font-mono text-[9px] font-bold ${
          isSelected
            ? "border-emerald-400 bg-emerald-500 text-white dark:text-slate-950"
            : "border-slate-300 text-slate-400 dark:border-slate-600 dark:text-slate-500"
        }`.trim()}
      >
        {isSelected ? "✓" : index + 1}
      </span>

      <SearchableOptionRowContent dense={dense} item={item} />

      {item.meta ? (
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-widest text-slate-500 dark:text-slate-400">
          {item.meta}
        </span>
      ) : null}
    </button>
  );
};

const SearchableOptionRowContent: FC<{
  dense?: boolean;
  item: SearchableOptionItem;
}> = ({ dense = false, item }) => {
  const optionHeader = (
    <div className="flex items-center gap-2">
      <p className="truncate font-mono text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
        {item.label}
      </p>
      {item.badge ? (
        <Badge variant="default" size="sm">
          {item.badge}
        </Badge>
      ) : null}
    </div>
  );
  const optionDescription = item.description ? (
    <p
      className={`text-slate-600 dark:text-slate-300 ${
        dense ? "text-[10px] leading-snug" : "mt-1 text-xs"
      }`}
    >
      {item.description}
    </p>
  ) : null;

  return (
    <div className="min-w-0 flex-1">
      {optionHeader}
      {optionDescription}
    </div>
  );
};

export const SearchableOptionList: FC<SearchableOptionListProps> = ({
  items,
  selectedValue,
  onSelect,
  label,
  searchPlaceholder = "Search...",
  emptyMessage = "No matches found.",
  disabled = false,
  className = "",
  dense = false,
  maxHeightClassName = dense ? "max-h-56" : "max-h-72",
}) => {
  const inputId = useId();
  const listRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return items;
    }

    return items.filter((item) => {
      const haystacks = [
        item.label,
        item.description,
        item.meta,
        ...(item.keywords ?? []),
      ];
      return haystacks.some(
        (candidate) =>
          typeof candidate === "string" &&
          candidate.toLowerCase().includes(normalizedQuery),
      );
    });
  }, [items, query]);

  useEffect(() => {
    if (filteredItems.length === 0) {
      setActiveIndex(0);
      return;
    }

    const selectedIndex = filteredItems.findIndex(
      (item) => item.value === selectedValue,
    );
    setActiveIndex(selectedIndex !== -1 ? selectedIndex : 0);
  }, [filteredItems, selectedValue]);

  useEffect(() => {
    const target = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    target?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (filteredItems.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((current) =>
        current >= filteredItems.length - 1 ? 0 : current + 1,
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? filteredItems.length - 1 : current - 1,
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const activeItem = filteredItems[activeIndex];
      if (activeItem && !activeItem.disabled && !disabled) {
        onSelect(activeItem.value);
      }
    }
  };

  return (
    <div className={className}>
      <div className="mb-1.5 flex items-center justify-between gap-3">
        {label ? (
          <label
            htmlFor={inputId}
            className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400"
          >
            {label}
          </label>
        ) : (
          <span />
        )}
        <Badge variant="status" size="sm">
          {filteredItems.length}/{items.length}
        </Badge>
      </div>

      <div className="rounded-none border border-slate-300 bg-white dark:border-slate-700 dark:bg-arti-background">
        <div
          className={`flex items-center gap-2 border-b border-slate-300 dark:border-slate-700 ${
            dense ? "px-2 py-1" : "px-3 py-2"
          }`}
        >
          <svg
            className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35m1.6-5.15a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            id={inputId}
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            disabled={disabled}
            className="w-full bg-transparent font-mono text-xs text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-50 dark:text-slate-100 dark:placeholder:text-slate-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-700 dark:hover:text-slate-200"
            >
              Clear
            </button>
          )}
        </div>

        <div
          ref={listRef}
          role="listbox"
          aria-disabled={disabled}
          className={`custom-scrollbar overflow-y-auto p-1.5 ${maxHeightClassName}`.trim()}
        >
          {filteredItems.length === 0 ? (
            <div
              className={
                dense ? "px-2 py-3 text-center" : "px-2 py-6 text-center"
              }
            >
              <Text className="text-slate-500 dark:text-slate-400">
                {emptyMessage}
              </Text>
            </div>
          ) : (
            <div className="space-y-1">
              {filteredItems.map((item, index) => (
                <SearchableOptionRow
                  key={item.value}
                  activeIndex={activeIndex}
                  dense={dense}
                  disabled={disabled}
                  index={index}
                  item={item}
                  onHover={setActiveIndex}
                  onSelect={onSelect}
                  selectedValue={selectedValue}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
